import {forwardRef, ReactNode, Ref, useContext, useId} from 'react';
import './flat-select-input.scss';
import {
  FormControl,
  SelectProps as MUISelectProps,
  Select,
  MenuItem,
  Slide,
  Typography,
  Box,
} from '@mui/material';
import {Pallete} from '../../theme/bluebird';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {ThemeMode} from '../../enums/theme-mode';
import {Tooltip} from '../tooltip/tooltip';

type FlatInputProps = {
  label?: string;
  errors?: string;
  options: Array<{value: any; label: string}>;
  disabledTooltip?: string | ReactNode;
} & MUISelectProps;

export const FlatSelectInput = forwardRef(
  (
    {label, errors, options, required, disabledTooltip, ...rest}: FlatInputProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const appContext: AppContextType = useContext(AppContext);

    const hasError = !!errors;

    const id = useId();

    const uniqueId = rest.id ?? `select_input_${id}`;

    const MenuItemStyles = {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '14px',
      height: '42px',
      backgroundColor:
        appContext.state.currentThemeMode === ThemeMode.Light
          ? Pallete.white.base
          : Pallete.gray.base,
      color: Pallete.gray.base,
    };

    return (
      <FormControl className={`flat-select-input ${rest.disabled ? 'disabled' : ''}`} fullWidth>
        {label && (
          <Box className="label-container">
            <Typography component="label" htmlFor={uniqueId} noWrap>
              {label}
            </Typography>
            {required && !rest.disabled && <Typography component="span">*</Typography>}
          </Box>
        )}
        <Tooltip.NotAllowedIf
          condition={rest.disabled && !!disabledTooltip}
          title={disabledTooltip}
          sx={{display: 'block', width: '100%', height: '42px'}}
        >
          <Select
            id={uniqueId}
            ref={ref}
            label={label}
            variant="filled"
            sx={{
              width: '100%',
              pointerEvents: rest.disabled ? 'none' : undefined,
              fontFamily: 'Roboto, sans-serif',
              fontWeight: rest.value === '' ? 400 : 500,
              fontSize: '14px',
              height: '42px',
              paddingBottom: '15px',
              overflow: 'hidden',
              color: rest.value === '' ? Pallete.gray.grayInput : Pallete.gray.base,
              backgroundColor: Pallete.white.darker,
              '&:hover': {
                color: '#464646',
                backgroundColor: Pallete.white.darker,
              },
              '&:active': {
                color: '#464646',
                backgroundColor: Pallete.white.darker,
              },
              '&.Mui-focused': {
                backgroundColor: Pallete.white.darker,
              },
              fieldset: {
                border: '0px!important',
                '&:hover': {
                  background: 'transparent',
                },
              },
            }}
            error={hasError}
            {...rest}
          >
            {rest.displayEmpty && (
              <MenuItem value="" sx={{...MenuItemStyles, color: Pallete.gray.grayInput}}>
                <span>{rest.placeholder}</span>
              </MenuItem>
            )}
            {options.map((option, index) => {
              const selected = option.value === rest.value;

              return (
                <MenuItem
                  {...option}
                  sx={{...MenuItemStyles, fontWeight: selected ? 500 : 400}}
                  key={index}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
        </Tooltip.NotAllowedIf>
        <Slide direction="right" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);

export default FlatSelectInput;
